/** Native complementary mappings only. No stock-count or supplier-price gates. */
export function rankComplements(entries, excludedIds, limit = 2) {
  const excluded = new Set(excludedIds.map(String));
  excluded.add('11190650995030');
  const merged = new Map();
  for (const entry of entries) {
    const id = String(entry.id);
    if (excluded.has(id) || !Number.isInteger(entry.rank) || entry.rank < 0) continue;
    const current = merged.get(id);
    if (current) {
      current.rank = Math.min(current.rank, entry.rank);
      current.order = Math.min(current.order, entry.order);
      current.anchors.add(String(entry.anchor));
    } else merged.set(id, { ...entry, id, anchors: new Set([String(entry.anchor)]) });
  }
  return [...merged.values()].sort((a, b) => a.rank - b.rank || b.anchors.size - a.anchors.size || a.order - b.order || a.id.localeCompare(b.id)).slice(0, limit);
}

// Simultaneous product/drawer requests share only in-flight reads; no stale stock cache.
const reads = new Map();
let instanceNumber = 0;
function readResults(url) {
  if (!reads.has(url)) reads.set(url, fetch(url, { credentials: 'same-origin', cache: 'no-store' }).then(response => {
    if (!response.ok) throw new Error('Recommendations unavailable');
    return response.text();
  }).finally(() => reads.delete(url)));
  return reads.get(url);
}

if (typeof window !== 'undefined' && !customElements.get('spk-complements')) {
  function statusRegion() {
    let region = document.getElementById('spk-complements-status');
    if (!region) {
      region = document.createElement('div');
      region.id = 'spk-complements-status';
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      document.body.append(region);
    }
    return region;
  }
  function announce(message) {
    const region = statusRegion();
    region.textContent = '';
    requestAnimationFrame(() => { region.textContent = message; });
  }
  function recoverDrawerFocus(event) {
    const source = event.target.closest?.('spk-complements[data-context="cart"]');
    const drawer = source?.closest('cart-drawer');
    if (!drawer?.hasAttribute('open') || !source.contains(document.activeElement)) return;
    // The selected card disappears after addition. Move focus to the checkout
    // only after Enterprise has replaced the drawer with the new cart response.
    let timeout;
    const observer = new MutationObserver(() => {
      if (drawer.contains(source)) return;
      observer.disconnect();
      clearTimeout(timeout);
      if (drawer.hasAttribute('open') && (document.activeElement === document.body || source.contains(document.activeElement))) {
        (drawer.querySelector('[name="checkout"]') || drawer).focus();
      }
    });
    observer.observe(drawer, { childList: true, subtree: true });
    timeout = setTimeout(() => observer.disconnect(), 3000);
  }
  for (const name of ['on:cart:add', 'on:line-item:change', 'on:cart:error']) {
    document.addEventListener(name, event => {
      const source = event.target.closest?.('spk-complements');
      if (!source) return;
      const title = event.target.closest('.spk-complements__card')?.querySelector('.spk-complements__title')?.textContent.trim();
      if (name === 'on:cart:error') {
        if (!event.detail?.error) announce('Produkten kunde inte läggas till. Försök igen.');
      } else if (title) announce(`${title} har lagts i varukorgen.`);
      recoverDrawerFocus(event);
    });
  }
  class SpkComplements extends HTMLElement {
    connectedCallback() {
      statusRegion();
      this.instance = ++instanceNumber;
      this.generation = 0;
      this.life = new AbortController();
      this.seed = JSON.parse(this.querySelector('[data-spk-seed]').textContent);
      this.list = this.querySelector('.spk-complements__list');
      this.addEventListener('change', event => this.chooseVariant(event), { signal: this.life.signal });
      // Enterprise rerenders the drawer on native add/quantity/remove operations.
      // The product-page instance also needs new cart exclusions after those events.
      for (const name of ['on:cart:add', 'on:line-item:change']) {
        document.addEventListener(name, () => this.refresh(), { signal: this.life.signal });
      }
      if (this.dataset.context === 'cart') {
        const drawer = this.closest('cart-drawer');
        if (drawer?.hasAttribute('open')) this.refresh();
        else {
          this.drawerObserver = new MutationObserver(() => {
            if (drawer?.hasAttribute('open')) this.refresh();
          });
          if (drawer) this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['open'] });
        }
      } else this.refresh();
    }

    disconnectedCallback() {
      this.generation++;
      this.life?.abort();
      this.drawerObserver?.disconnect();
    }

    chooseVariant(event) {
      if (!event.target.matches('.spk-complements__variant')) return;
      const card = event.target.closest('.spk-complements__card');
      const option = event.target.selectedOptions[0];
      const button = card.querySelector('[name="add"]');
      button.disabled = !option.value;
      const title = card.querySelector('.spk-complements__title').textContent.trim();
      button.setAttribute('aria-label', `Lägg till ${title}${option.value ? `, ${option.textContent.trim()}` : ''}`);
      if (option.value) {
        // The installed money/translation integration can wrap formatted values.
        // Use inert text extraction so its markup never becomes visible price text.
        const priceText = new DOMParser().parseFromString(option.dataset.price, 'text/html').body.textContent;
        card.querySelector('.spk-complements__price').textContent = priceText;
        announce(`${title}: ${option.textContent.trim()}, ${priceText}.`);
        const image = card.querySelector('img');
        if (image) image.hidden = !option.dataset.image;
        if (image && option.dataset.image) {
          image.removeAttribute('srcset');
          image.src = option.dataset.image;
        }
      }
    }

    async refresh() {
      const generation = ++this.generation;
      // Hide old suggestions immediately; a delayed response must not restore stale results.
      this.hidden = true;
      try {
        const anchors = [...new Map(this.seed.anchors.filter(a => a.ids?.length).map(a => [String(a.id), a])).values()];
        if (!anchors.length) { this.list.replaceChildren(); return; }
        const cartResponse = await fetch(this.dataset.cartUrl, { credentials: 'same-origin', cache: 'no-store' });
        if (!cartResponse.ok) throw new Error('Cart unavailable');
        const cart = await cartResponse.json();
        const excluded = cart.items.map(item => String(item.product_id));
        excluded.push(String(this.seed.viewedId));
        const entries = [];
        let next = 0;
        // Bound concurrent requests while still considering every mapped cart product.
        await Promise.all(Array.from({ length: Math.min(4, anchors.length) }, async () => {
          while (next < anchors.length) {
            const order = next++;
            const anchor = anchors[order];
            if (this.dataset.context === 'cart' && !excluded.includes(String(anchor.id))) continue;
            const url = new URL(anchor.url, location.origin);
            if (url.origin !== location.origin) continue;
            url.search = '';
            url.searchParams.set('section_id', 'spk-complementary-results');
            try {
              const html = await readResults(url.href);
              const doc = new DOMParser().parseFromString(html, 'text/html');
              for (const node of doc.querySelectorAll('[data-product-id]')) {
                const id = node.dataset.productId;
                const rank = anchor.ids.map(String).indexOf(id);
                // Explicit mappings form an allowlist as well as an editorial order.
                if (rank >= 0) entries.push({ id, anchor: anchor.id, rank, order, node });
              }
            } catch { /* No relevant fallback on a failed request. */ }
          }
        }));
        if (generation !== this.generation || !this.isConnected) return;
        const cards = rankComplements(entries, excluded).map(entry => {
          const card = entry.node.cloneNode(true);
          // A product may appear in both placements; each form/label gets its own IDs.
          for (const element of card.querySelectorAll('[id]')) {
            const original = element.getAttribute('id');
            element.setAttribute('id', `${original}-${this.instance}`);
            for (const label of card.querySelectorAll('label')) {
              if (label.htmlFor === original) label.htmlFor = element.id;
            }
          }
          return card;
        });
        this.list.replaceChildren(...cards);
        this.hidden = !cards.length;
      } catch {
        if (generation === this.generation) { this.list.replaceChildren(); this.hidden = true; }
      }
    }
  }
  customElements.define('spk-complements', SpkComplements);
}
